import { ProtocolSettings } from "../Protocols/ProtocolSettings";
import { ProtocolSettingsFactory } from "../Protocols/ProtocolSettingsFactory";
import { SnmpSettings } from "../Protocols/SnmpSettings";
import { PingSettings } from "../Protocols/PingSettings";
import { WindowsSettings } from "../Protocols/WindowsSettings";
import { SshSettings } from "../Protocols/SshSettings";
import { TelnetSettings } from "../Protocols/TelnetSettings";
import { VimSettings } from "../Protocols/VimSettings";
import { PrtgSettings } from "../Protocols/PrtgSettings";
import { List } from "../Utils/List";
import { HashSet } from "../Utils/HashSet";
import { ObjectSerializer } from "../Serialization/ObjectSerializer";
import { ObjSerUtils } from "../Serialization/ObjSerUtils";
import { ValueEncoder } from "../Utils/ValueEncoder";

export class ProtocolProfileEntry {

	public ProtocolName: string;
	public ProtocolSettings: ProtocolSettings;

	constructor(protoName: string, protoSettings: ProtocolSettings) {
		this.ProtocolName = protoName;
		this.ProtocolSettings = protoSettings;
	}
}


/// <summary>
/// Collection of ProtocolSettings assigned to a device.
/// At most one set of ProtocolSettings are stored for each Protocol.
/// </summary>
export class ProtocolProfile implements ObjectSerializer {

	private Entries: List<ProtocolProfileEntry>;

	constructor() {
		this.Entries = new List<ProtocolProfileEntry>();
	}

	public get ProtocolNames(): HashSet<string> {
		let result = new HashSet<string>();

		this.Entries.ForEach((value: ProtocolProfileEntry) => {
			result.Add(value.ProtocolName);
		}, this);

		return result;
	}

	/// <summary>
	/// Adds a CLONE of the specified settings to the protocol profile.
	/// </summary>
	/// <param name="protoName">The name of the protocol to which the settings apply</param>
	/// <param name="protoSettings">The settings to be added</param>
	public Add(protoName: string, protoSettings: ProtocolSettings): void {

		// Remove the old settings for this protocol, if any
		let oldSettings: ProtocolSettings = this.FindSettingsForProtocol(protoName);
		if (oldSettings != null)
			this.RemoveProtocolSettings(oldSettings);

		// Add new settings
		var entry = new ProtocolProfileEntry(protoName, protoSettings.Clone());
		this.Entries.Add(entry);
	}

	public RemoveProtocolName(protoName: string): void {
		let entry: ProtocolProfileEntry = this.FindEntryForProtocol(protoName);
		if (entry != null)
			this.Entries.Remove(entry);
	}

	public RemoveProtocolSettings(protoSettings: ProtocolSettings): void {
		let entry: ProtocolProfileEntry = this.FindEntryForSettings(protoSettings);
		if (entry != null)
			this.Entries.Remove(entry);
	}

	public ContainsProtocolName(protoName: string): boolean {
		return (this.FindEntryForProtocol(protoName) != null);
	}

	public ContainsProtocolSettings(protoSettings: ProtocolSettings): boolean {
		return (this.FindEntryForSettings(protoSettings) != null);
	}

	public ContainsProtocolSettingsWithGuid(protoSettingsGUID: string): boolean {
		let guid: string = protoSettingsGUID.toLowerCase();

		let result: boolean = false;
		this.Entries.ForEach((entry) => {
			if (entry.ProtocolSettings.GUID.toLowerCase() == guid)
				result = true;
		}, this);

		return result;
	}

	public get Count(): number {
		return this.Entries.Count;
	}

	public Clear(): void {
		this.Entries.Clear();
	}

	private FindEntryForSettings(protoSettings: ProtocolSettings): ProtocolProfileEntry {
		let result: ProtocolProfileEntry = null;

		this.Entries.ForEach((entry) => {
			if (entry.ProtocolSettings == protoSettings)
				result = entry;
		}, this);

		return result;
	}

	public FindEntryForProtocol(protoName: string): ProtocolProfileEntry {
		let result: ProtocolProfileEntry = null;

		this.Entries.ForEach((entry) => {
			if (entry.ProtocolName == protoName)
				result = entry;
		}, this);

		return result;
	}

	public FindSettingsForProtocol(protoName: string): ProtocolSettings {
		let entry: ProtocolProfileEntry = this.FindEntryForProtocol(protoName);
		return ((entry != null) ? entry.ProtocolSettings : null);
	}

	public ForEach(callbackfn: (value: ProtocolProfileEntry, index: number, array: ProtocolProfileEntry[]) => void, thisArg?: any): void {
		this.Entries.ForEach(callbackfn, thisArg);
	}

	public Some(callbackfn: (value: ProtocolProfileEntry, index: number, array: ProtocolProfileEntry[]) => boolean, thisArg?: any): boolean {
		return this.Entries.Some(callbackfn, thisArg);
	}

	public Every(callbackfn: (value: ProtocolProfileEntry, index: number, array: ProtocolProfileEntry[]) => boolean, thisArg?: any): boolean {
		return this.Entries.Every(callbackfn, thisArg);
	}

	public Write(obj: any, context: any): void {
		obj.protocol_profile = {};
		obj = obj.protocol_profile;

		obj.entries = [];
		ObjSerUtils.WriteCollection(obj.entries, this.Entries, (entry: ProtocolProfileEntry) => {
			let protoSettingsObj = {};
			entry.ProtocolSettings.Write(protoSettingsObj, context);

			let elem = {
				entry: {
					protocol_name: entry.ProtocolName,
					protocol_settings: protoSettingsObj
				}
			};

			return elem;
		}, context);	
	}

	public Read(obj: any, context: any): void {
		if (!obj.protocol_profile)
			throw new Error();
		obj = obj.protocol_profile;

		this.Clear();

		if (obj.entries !== undefined) {
			obj.entries.forEach((value: any) => {
				let protoName = value.entry.protocol_name;
				let protoSettingsObj = value.entry.protocol_settings;

				let protoSettings: ProtocolSettings = ProtocolSettingsFactory.Create(protoSettingsObj);
				if (!protoSettings)
					throw new Error("Read Error: Invalid protocol settings");
				protoSettings.Read(protoSettingsObj, context);

				this.Add(protoName, protoSettings);
			}, this);
		}
	}

}